import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingCartSimple } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { api, formatApiErrorDetail } from "../../lib/api";

export function RepBuyLeadsTab() {
  const [packages, setPackages] = useState([]);
  const [buyingId, setBuyingId] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  useEffect(() => {
    api.get("/rep/lead-packages").then(({ data }) => setPackages(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("checkout_session_id") || localStorage.getItem("igc_rep_pending_checkout_session");
    if (!sessionId) return;
    setCheckingStatus(true);
    let attempts = 0;
    const poll = () => {
      api.get(`/rep/lead-packages/checkout/status/${sessionId}`).then(({ data }) => {
        if (data.payment_status === "paid") {
          toast.success("Payment received — your lead package purchase is confirmed. Our team will load your leads shortly.");
          setCheckingStatus(false);
          localStorage.removeItem("igc_rep_pending_checkout_session");
          window.history.replaceState({}, "", "/rep?tab=buy-leads");
        } else if (attempts < 5) {
          attempts += 1;
          setTimeout(poll, 2000);
        } else {
          setCheckingStatus(false);
          localStorage.removeItem("igc_rep_pending_checkout_session");
        }
      }).catch(() => setCheckingStatus(false));
    };
    poll();
  }, []);

  const buyPackage = async (pkg) => {
    setBuyingId(pkg.id);
    try {
      const { data } = await api.post(`/rep/lead-packages/${pkg.id}/checkout`, { origin_url: window.location.origin });
      localStorage.setItem("igc_rep_pending_checkout_session", data.session_id);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBuyingId(null);
    }
  };

  return (
    <div data-testid="rep-buy-leads-tab" className="space-y-6">
      {checkingStatus && (
        <div className="bg-igc-gold/10 border border-igc-gold/30 p-4 text-igc-goldText text-sm" data-testid="rep-checkout-status-checking">
          Confirming your payment…
        </div>
      )}
      <div>
        <h3 className="font-serif text-lg text-igc-cream tracking-tight mb-1" title="This page lets you buy extra curated leads out of your own pocket, on top of the free leads IGC assigns you, if you want to work more volume.">Lead Packages</h3>
        <p className="text-igc-muted text-sm mb-4">Purchase a package of curated leads for yourself — they're loaded into your own folder shortly after purchase.</p>
      </div>

      {packages.length === 0 && (
        <p className="text-igc-muted text-sm bg-igc-surface border border-igc-gold/10 p-5" data-testid="rep-buy-leads-empty">No lead packages are available for purchase right now — check back soon.</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {packages.map((p) => (
          <div key={p.id} className="bg-igc-surface border border-igc-gold/15 p-5 flex flex-col justify-between" data-testid={`rep-buy-lead-package-${p.id}`} title={`A bundle of ${p.lead_count} ${p.product_interest} leads for $${p.price.toLocaleString()}. Once you pay, IGC loads these leads directly into your queue — no manual steps needed on your end.`}>
            <div>
              <span className="text-igc-goldText text-xs uppercase tracking-widest">{p.product_interest}</span>
              <p className="font-serif text-xl text-igc-cream mt-1">{p.name}</p>
              <p className="text-igc-muted text-sm mt-2">{p.lead_count} leads{p.description ? ` — ${p.description}` : ""}</p>
              <p className="font-serif text-2xl text-igc-goldText mt-3">${p.price.toLocaleString()}</p>
            </div>
            <Button data-testid={`rep-buy-lead-package-button-${p.id}`} onClick={() => buyPackage(p)} disabled={buyingId === p.id} title="Takes you to a secure checkout page to pay for this lead package with a card. After payment, IGC loads the leads into your queue shortly after." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black mt-4 flex items-center justify-center gap-2">
              <ShoppingCartSimple size={16} weight="bold" /> {buyingId === p.id ? "Redirecting…" : "Buy Now"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
